// Stable Seedbed track ids, carried in the Live track name.
//
// Ableton auto-names a track with a number ("11-BOS_HDT…", "34 Wavetable") and
// RENUMBERS it whenever the roster changes. Sync identity ultimately bottoms out
// in names, so the one event that moves a track is the same event that renames
// it — name matching fails exactly when it is needed.
//
// The SDK offers nothing else to key on. `Track` exposes `name` as its only
// writable string (no colour, no comment, no user data), and docs/sdk/handles.md
// says a handle is REALLOCATED when a track moves and invalidated when the Set
// closes. So we mint an id and put it in the name, which is the only durable
// writable per-track string there is.
//
// Stamping does two things, and the second one is the actual fix:
//
//   1. It gives every track an identity that survives a move, a rename and a
//      reload — the thing bindings can key on instead of a roster position.
//   2. It FREEZES the name. Live only renumbers names it generated itself, so
//      writing an explicit name stops Live touching it again, forever.
//
// (2) is why removing the number is cosmetic rather than load-bearing, and why
// `stripAutoNumber` is allowed to be conservative: a name we decline to strip
// still stops renumbering the moment it is stamped.
//
// Pure data. No SDK, no `node:` imports — the extension, workers/ and viz/ all
// import this.

/** Delimiters live here alone so the ASCII fallback is a one-line change. */
const OPEN = "⟨";
const CLOSE = "⟩";
const PREFIX = "sb:";

/** A real id: exactly 6 lowercase hex, anchored to the end of the name. */
const STRICT = new RegExp(`\\s*${OPEN}${PREFIX}([0-9a-f]{6})${CLOSE}$`);

/**
 * Anything occupying the token slot — a token the user has edited by hand, most
 * likely by half-deleting it. Not a valid id, but not part of the name they
 * meant either: without this, re-stamping "Kick ⟨sb:zzz⟩" would append a second
 * token and give "Kick ⟨sb:zzz⟩ ⟨sb:a3f19c⟩".
 */
const LENIENT = new RegExp(`\\s*${OPEN}${PREFIX}[^${CLOSE}]*${CLOSE}$`);

/**
 * Live's auto-name: a number, a separator, then the device or sample name.
 *
 * Two separators are in use — "52-Group" and "34 Wavetable" both appear in the
 * same real Set — and the number is NOT the track's position. It is frozen when
 * the track is created or a sample is dropped on it and then goes stale, so the
 * captured Set has 11 at roster position 4 and 56 at position 35. Any rule that
 * validates the number against the current position strips almost nothing.
 */
const DASH_FORM = /^\s*(\d{1,3})-\s*(?=\S)/;
const SPACE_FORM = /^\s*(\d{1,3})\s+(?=\S)/;

export interface ParsedTrackName {
  /** The name without its token — what a human would call the track. */
  display: string;
  /** The Seedbed id, or null if this track has never been stamped. */
  token: string | null;
}

export interface StripOptions {
  /**
   * The largest number that could plausibly be a track ordinal in this Set —
   * pass the track count. Required for the space-separated form to be stripped
   * at all; see `stripAutoNumber`.
   */
  maxOrdinal?: number;
}

/** Read a raw Live track name into its display half and its id. */
export function parseTrackName(raw: string): ParsedTrackName {
  const strict = STRICT.exec(raw);
  if (strict) {
    const display = raw.slice(0, strict.index);
    // A track named nothing but a token has no display half to keep, and
    // returning "" would write an empty name back to Live.
    if (display.length > 0) return { display, token: strict[1]! };
    return { display: raw, token: null };
  }

  const lenient = LENIENT.exec(raw);
  if (lenient) {
    const display = raw.slice(0, lenient.index);
    if (display.length > 0) return { display, token: null };
  }

  return { display: raw, token: null };
}

/** Stamp a display name with an id. */
export function formatTrackName(display: string, token: string): string {
  return `${display} ${OPEN}${PREFIX}${token}${CLOSE}`;
}

/**
 * A rename that keeps the track's id. Surfaces that show the display half
 * (the browser's lane header) propose only what the user typed; the token is
 * identity, so it rides along. A name that brings its own token, or a track
 * that was never stamped, passes through as given.
 */
export function renameKeepingToken(currentRaw: string, nextRaw: string): string {
  if (parseTrackName(nextRaw).token !== null) return nextRaw;
  const token = parseTrackName(currentRaw).token;
  const display = nextRaw.trim();
  if (token === null || display.length === 0) return nextRaw;
  return formatTrackName(display, token);
}

/** A fresh id: 6 lowercase hex. */
export function mintToken(): string {
  let out = "";
  for (let i = 0; i < 6; i++) out += Math.floor(Math.random() * 16).toString(16);
  return out;
}

/** The name with its token removed. The single strip-for-display entry point. */
export function displayNameOf(raw: string): string {
  return parseTrackName(raw).display;
}

/** Anything in the token slot, wherever it sits in the name. */
const ANYWHERE = new RegExp(`\\s*${OPEN}${PREFIX}[^${CLOSE}]*${CLOSE}`, "g");

/**
 * Every token stripped, not just a trailing one — for DISPLAY only.
 *
 * `parseTrackName` is anchored on purpose: identity must read the token the
 * stamp wrote and nothing else. But a name can carry one mid-string once
 * something has been appended after the stamp — a bounce gives
 * "Shakers ⟨sb:46adf6⟩ (Bounce)" — and the anchored parse leaves that token
 * on screen. A pad or a lane header wants the human half whatever order the
 * suffixes landed in.
 */
export function stripTokens(raw: string): string {
  return raw.replace(ANYWHERE, "").replace(/\s+/g, " ").trim() || raw;
}

export interface AutoNumber {
  /** The numeral as written. Frozen at creation/sample-drop and possibly stale —
   * never trust it as a position. */
  number: number;
  separator: "-" | " ";
  /** What follows the number — the device or sample name. */
  rest: string;
}

/**
 * Read Live's auto-number off the front of a display name, if one is there.
 * Pure parsing — deciding whether to STRIP it is `stripAutoNumber`'s job,
 * because the space form is ambiguous ("34 Wavetable" vs "808 Sub").
 */
export function parseAutoNumber(display: string): AutoNumber | null {
  const dash = DASH_FORM.exec(display);
  if (dash) {
    return { number: Number(dash[1]), separator: "-", rest: display.slice(dash[0].length) };
  }
  const space = SPACE_FORM.exec(display);
  if (space) {
    return { number: Number(space[1]), separator: " ", rest: display.slice(space[0].length) };
  }
  return null;
}

/**
 * Remove Live's auto-generated number from a name.
 *
 * The dash form is stripped unconditionally — "52-Group" is not a name anyone
 * types. The SPACE form is not, unless the caller bounds the ordinal, because
 * "34 Wavetable" (Live's) and "808 Sub" (a producer's) are the same shape and
 * nothing in the string tells them apart. Guessing wrong mangles a name the user
 * chose, and buys nothing: stamping freezes the name either way.
 *
 * Called exactly once per track, at the moment its token is minted — a stamped
 * name is never re-parsed for numbering. So a wrong strip is a one-time, visible,
 * user-undoable event rather than a fight that repeats every poll.
 */
export function stripAutoNumber(display: string, options: StripOptions = {}): string {
  const auto = parseAutoNumber(display);
  if (!auto) return display;
  if (auto.separator === "-") return auto.rest;
  return auto.number <= (options.maxOrdinal ?? 0) ? auto.rest : display;
}
